import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { finalize, mergeMap, Observable, Subject, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { responseStatus } from '../consts/response-status';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  private tokenRefreshSource = new Subject<boolean>();
  private refreshTokenInProgress = false;
  private isRequestWasFrozen = false;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap(response => {
        if (response instanceof HttpResponse && this.isRequestWasFrozen) {
          this.isRequestWasFrozen = false;
          this.authService.setAuthState(true);
        }
      }),
      catchError(err => {
        if (
          err instanceof HttpErrorResponse &&
          err.status === responseStatus.unauthorized &&
          // we should go to another catch error if it's error in token response
          !err.url?.includes('token')
        ) {
          return this.checkRefreshToken().pipe(
            mergeMap(() => {
              this.isRequestWasFrozen = true;
              return next.handle(request);
            })
          );
        }

        err.isProceed = true;
        return throwError(err);
      })
    );
  }

  private checkRefreshToken(): Observable<void | null> {
    if (this.refreshTokenInProgress) {
      return new Observable<void>(observer => {
        const subscribe = this.tokenRefreshSource.asObservable().subscribe(() => {
          observer.next();
          observer.complete();
          subscribe.unsubscribe();
        });
      });
    }

    this.refreshTokenInProgress = true;

    return this.authService.refreshAuthToken().pipe(
      catchError(({ error }) => {
        if (error instanceof HttpErrorResponse && error.status === responseStatus.unauthorized) {
          this.authService.redirectOkta();
        }

        return throwError(error);
      }),
      tap(() => {
        this.tokenRefreshSource.next(true);
      }),
      finalize(() => {
        this.refreshTokenInProgress = false;
      })
    );
  }
}
